import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, Send } from 'lucide-react';
import {
  supabase, PageHead, DataTable, Chips, Pill, UserCell, Modal, useRows, useUsers, useAction, notify, audit, fmtDateTime, userName,
} from '../adminShared';

const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];
const PRIORITY_KIND = { urgent: 'bad', high: 'bad', medium: 'warn', low: '' };

export default function SupportAdmin({ refreshCounts }) {
  const users = useUsers();
  const tickets = useRows('support_tickets', { order: 'updated_at' });
  const [status, setStatus] = useState('open');
  const [open, setOpen] = useState(null);

  const counts = useMemo(() => tickets.rows.reduce((m, t) => { m[t.status] = (m[t.status] || 0) + 1; return m; }, {}), [tickets.rows]);
  const list = status === 'all' ? tickets.rows : tickets.rows.filter(t => t.status === status);

  return (
    <>
      <PageHead title="Support tickets" sub="Reply to users and keep each ticket's status up to date. Users see replies on their Support page." />
      <DataTable rows={list} loading={tickets.loading} error={tickets.error} missing={tickets.missing} table="support_tickets" onRowClick={setOpen} empty="No tickets here"
        toolbar={<Chips value={status} onChange={setStatus} items={[
          ...STATUSES.map(s => ({ key: s, label: `${s.replace('_', ' ')} (${counts[s] || 0})` })), { key:'all', label:'All' },
        ]} />}
        columns={[
          { key:'ticket_ref', label:'Ticket', render:t => <span className="ad-mono">{t.ticket_ref || String(t.id).slice(0, 8)}</span> },
          { key:'subject', label:'Subject', wrap:true, render:t => <strong style={{ fontWeight:600 }}>{t.subject}</strong> },
          { key:'user', label:'User', render:t => <UserCell user={users[t.user_id]} id={t.user_id} /> },
          { key:'category', label:'Category' },
          { key:'priority', label:'Priority', render:t => <span className={`ad-pill ${PRIORITY_KIND[t.priority] ?? ''}`}>{t.priority || '—'}</span> },
          { key:'status', label:'Status', render:t => <Pill status={t.status} /> },
          { key:'updated_at', label:'Last update', render:t => fmtDateTime(t.updated_at || t.created_at) },
        ]} />
      {open && <Thread ticket={open} user={users[open.user_id]} onClose={() => setOpen(null)} onChanged={() => { tickets.reload(); refreshCounts?.(); }} />}
    </>
  );
}

const ATTACH_BUCKET = 'ticket-attachments';
const fmtBytes = (n) => n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`;

// Files a user attached to a message. The bucket is private, so each file gets a short-lived signed link.
function Attachments({ items }) {
  const [urls, setUrls] = useState({});
  const key = items.map(a => a.path).join('|');

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await supabase.storage.from(ATTACH_BUCKET).createSignedUrls(items.map(a => a.path), 3600);
      if (!alive || error || !data) return;
      const map = {};
      data.forEach(d => { if (d.signedUrl) map[d.path] = d.signedUrl; });
      setUrls(map);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:10 }}>
      {items.map(a => {
        const url = urls[a.path];
        const isImg = (a.type || '').startsWith('image/');
        if (isImg && url) {
          return (
            <a key={a.path} href={url} target="_blank" rel="noopener noreferrer" title={`${a.name} · ${fmtBytes(a.size || 0)}`}>
              <img src={url} alt={a.name} style={{ display:'block', maxWidth:220, maxHeight:160, borderRadius:8, border:'1px solid var(--border)' }} />
            </a>
          );
        }
        return (
          <a key={a.path} href={url || undefined} target="_blank" rel="noopener noreferrer"
            title={url ? 'Open file' : 'Loading…'}
            style={{
              display:'inline-flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:8, fontSize:13,
              background:'var(--surface)', border:'1px solid var(--border)', color:'inherit', textDecoration:'none',
              opacity: url ? 1 : 0.6, pointerEvents: url ? 'auto' : 'none',
            }}>
            <Paperclip size={14} /> {a.name} <span style={{ color:'var(--muted)' }}>· {fmtBytes(a.size || 0)}</span>
          </a>
        );
      })}
    </div>
  );
}

function Thread({ ticket, user, onClose, onChanged }) {
  const [msgs, setMsgs] = useState(null);
  const [reply, setReply] = useState('');
  const [status, setStatus] = useState(ticket.status);
  const [busy, run] = useAction();
  const endRef = useRef(null);

  const load = async () => {
    const { data } = await supabase.from('ticket_messages').select('*').eq('ticket_id', ticket.id).order('created_at', { ascending: true });
    setMsgs(data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ticket.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs]);

  const setTicketStatus = (s) => run('st', async () => {
    const now = new Date().toISOString();
    const { error } = await supabase.from('support_tickets')
      .update({ status: s, updated_at: now, resolved_at: ['resolved', 'closed'].includes(s) ? now : null }).eq('id', ticket.id);
    if (error) throw error;
    audit('set_status', 'support_tickets', ticket.id, { status }, { status: s });
    setStatus(s); onChanged();
  }, `Ticket marked ${s.replace('_', ' ')}`);

  const send = () => run('send', async () => {
    const body = reply.trim();
    const { error } = await supabase.from('ticket_messages').insert({ ticket_id: ticket.id, sender_id: null, is_agent: true, body });  // sender_id references users; agents aren't users
    if (error) throw error;
    const next = status === 'open' ? 'in_progress' : status;
    await supabase.from('support_tickets').update({ status: next, updated_at: new Date().toISOString() }).eq('id', ticket.id);
    setStatus(next);
    await notify(ticket.user_id, { title: `New reply on ${ticket.ticket_ref || 'your ticket'}`, description: body.slice(0, 140), category: 'system', icon_class: 'ti-headset', action_url: '/support' });
    setReply(''); load(); onChanged();
  }, 'Reply sent');

  return (
    <Modal title={ticket.subject} sub={`${ticket.ticket_ref || ''} · ${userName(user)} · ${ticket.category || ''}`} onClose={onClose} wide
      footer={<div style={{ display:'flex', gap:10, width:'100%', alignItems:'flex-end' }}>
        <textarea className="ad-textarea" style={{ minHeight:70, flex:1 }} placeholder="Write a reply…" value={reply} onChange={e => setReply(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && reply.trim()) send(); }} />
        <button className="ad-btn ad-btn-accent" disabled={!reply.trim() || busy === 'send'} onClick={send}><Send />Send</button>
      </div>}>
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <span style={{ color:'var(--muted)', fontSize:13.5 }}>Status</span>
        <select className="ad-select" style={{ width:'auto', height:34 }} value={status} disabled={busy === 'st'} onChange={e => setTicketStatus(e.target.value)}>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <span style={{ color:'var(--faint)', fontSize:13 }}>Opened {fmtDateTime(ticket.created_at)}</span>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {msgs === null ? <div className="ad-empty">Loading…</div> : msgs.length === 0 ? <div className="ad-empty">No messages yet.</div> : msgs.map(m => (
          <div key={m.id} style={{
            alignSelf: m.is_agent ? 'flex-end' : 'flex-start', maxWidth:'82%', padding:'10px 14px', borderRadius:14,
            background: m.is_agent ? 'var(--accent-dim)' : 'var(--surface2)', border:`1px solid ${m.is_agent ? 'rgba(226,174,74,.3)' : 'var(--border)'}`,
          }}>
            <div style={{ fontSize:12, color:'var(--muted)', marginBottom:4 }}>{m.is_agent ? 'Support team' : userName(user)} · {fmtDateTime(m.created_at)}</div>
            <div style={{ whiteSpace:'pre-wrap', fontSize:14.5 }}>{m.body}</div>
            {Array.isArray(m.attachments) && m.attachments.length > 0 && <Attachments items={m.attachments} />}
          </div>
        ))}
        <div ref={endRef} />
      </div>
    </Modal>
  );
}